import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import mediaAPI from '../../services/mediaAPI';
import { tenantAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import usePermission from '../../hooks/usePermission';
import './Media.css';

const TYPE_ICON = {
    image: 'fa-image',
    video: 'fa-video',
    audio: 'fa-music',
    document: 'fa-file-alt',
    file: 'fa-file',
};

const TYPE_FILTERS = [
    { value: '', label: 'All' },
    { value: 'image', label: 'Images' },
    { value: 'video', label: 'Videos' },
    { value: 'audio', label: 'Audio' },
    { value: 'document', label: 'Documents' },
];

const LIMIT = 30;

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

const Media = () => {
    const { canCreate, canDelete, canEdit } = usePermission('media');
    const isSuperadmin = useAuthStore((s) => s.admin?.role) === 'superadmin';
    // A superadmin has no tenant of their own — the server needs one for
    // every upload, so this picker is how they say whose library they're in.
    const [tenants, setTenants] = useState([]);
    const [tenantFilter, setTenantFilter] = useState('');
    const [media, setMedia] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
    const [typeFilter, setTypeFilter] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [mediaToDelete, setMediaToDelete] = useState(null);
    const [resizeTarget, setResizeTarget] = useState(null); // media item being resized
    const [resizeForm, setResizeForm] = useState({ width: '', height: '' });
    const [optimizingId, setOptimizingId] = useState(null);

    const fileInputRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page, limit: LIMIT };
            if (typeFilter) params.mediaType = typeFilter;
            if (isSuperadmin && tenantFilter) params.tenantId = tenantFilter;
            const res = await mediaAPI.getAll(params);
            setMedia(res.data.data.media || []);
            setPagination(res.data.data.pagination || null);
        } catch (error) {
            toast.error('Failed to fetch media');
        } finally {
            setLoading(false);
        }
    }, [page, typeFilter, tenantFilter, isSuperadmin]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!isSuperadmin) return;
        tenantAPI.getAll({ limit: 1000 })
            .then((res) => setTenants(res.data.data.tenants || []))
            .catch(() => toast.error('Failed to fetch tenants'));
    }, [isSuperadmin]);

    // Reset to page 1 whenever a filter changes.
    useEffect(() => { setPage(1); }, [typeFilter, tenantFilter]);

    const uploadFiles = async (files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;
        if (isSuperadmin && !tenantFilter) {
            toast.warning('Select a tenant first — uploads are stored per tenant');
            return;
        }

        setUploading(true);
        setUploadProgress({ done: 0, total: list.length });
        let successCount = 0;
        // Backend accepts one file per request (middlewares/mediaUpload.js) —
        // multi-file upload here is N sequential calls, not a backend change.
        for (let i = 0; i < list.length; i++) {
            try {
                await mediaAPI.upload(list[i], { tenantId: isSuperadmin ? tenantFilter : undefined });
                successCount++;
            } catch (error) {
                toast.error(`${list[i].name}: ${error.response?.data?.message || 'Upload failed'}`);
            }
            setUploadProgress({ done: i + 1, total: list.length });
        }
        setUploading(false);
        setUploadProgress(null);
        if (successCount > 0) {
            toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
            setPage(1);
            fetchData();
        }
    };

    const handleFileSelect = (e) => {
        uploadFiles(e.target.files);
        e.target.value = '';
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
    };

    const handleDelete = (id) => {
        setMediaToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!mediaToDelete) return;
        try {
            await mediaAPI.delete(mediaToDelete);
            toast.success('Media deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete media');
        } finally {
            setShowDeleteModal(false);
            setMediaToDelete(null);
        }
    };

    const openResize = (item) => {
        setResizeTarget(item);
        setResizeForm({ width: item.width || '', height: item.height || '' });
    };

    const confirmResize = async () => {
        if (!resizeTarget) return;
        const width = parseInt(resizeForm.width, 10);
        const height = parseInt(resizeForm.height, 10);
        if (!width && !height) {
            toast.warning('Enter a width or height');
            return;
        }
        try {
            await mediaAPI.resize(resizeTarget._id, { width: width || undefined, height: height || undefined });
            toast.success('Resized copy created');
            setResizeTarget(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Resize failed');
        }
    };

    const handleOptimize = async (item) => {
        setOptimizingId(item._id);
        try {
            await mediaAPI.optimize(item._id);
            toast.success('Optimized copy created');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Optimize failed');
        } finally {
            setOptimizingId(null);
        }
    };

    const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / LIMIT)) : 1;

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-photo-video"></i>
                            Media Library
                        </h1>
                        <p className="page-subtitle-modern">Images, video, audio, and documents for your captive portal</p>
                    </div>
                    {canCreate && (
                        <button
                            className="btn-primary-modern"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                            {uploading ? `Uploading ${uploadProgress?.done ?? 0}/${uploadProgress?.total ?? 0}...` : 'Upload Files'}
                        </button>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileSelect}
                        disabled={uploading}
                    />
                </div>
            </div>

            <div className="filters-section">
                {isSuperadmin && (
                    <div className="filter-group">
                        <select
                            className="filter-select"
                            value={tenantFilter}
                            onChange={(e) => setTenantFilter(e.target.value)}
                        >
                            <option value="">All Tenants (select one to upload)</option>
                            {tenants.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                        </select>
                    </div>
                )}
                <div className="filter-group">
                    <select
                        className="filter-select"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        {TYPE_FILTERS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {canCreate && (
                <div
                    className={`media-dropzone ${dragActive ? 'active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Drag & drop files here, or use the Upload Files button above</span>
                </div>
            )}

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Fetching media...</p>
                </div>
            ) : media.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-photo-video"></i>
                    </div>
                    <h3>No Media Uploaded</h3>
                    <p>Upload images, video, audio, or documents to get started.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {media.map(item => (
                        <div key={item._id} className="resource-card-modern">
                            <div style={{ height: '160px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {item.mediaType === 'image' ? (
                                    <img
                                        src={item.thumbnailUrl || item.url}
                                        alt={item.altText || item.originalFilename}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className={`fas ${TYPE_ICON[item.mediaType] || 'fa-file'} fa-3x text-muted`}></i>
                                )}
                            </div>
                            <div className="card-header">
                                <div className="resource-title">
                                    <h3 title={item.originalFilename}>{item.originalFilename || item.filename}</h3>
                                    <span className="resource-subtitle">
                                        {formatBytes(item.fileSize)} • {item.mediaType}
                                        {item.width && item.height ? ` • ${item.width}×${item.height}` : ''}
                                    </span>
                                </div>
                                <div className="card-actions">
                                    {canEdit && item.mediaType === 'image' && (
                                        <>
                                            <button
                                                className="action-btn-icon"
                                                title="Resize (creates a new copy)"
                                                onClick={() => openResize(item)}
                                            >
                                                <i className="fas fa-crop-alt"></i>
                                            </button>
                                            <button
                                                className="action-btn-icon"
                                                title="Optimize (creates a new copy)"
                                                onClick={() => handleOptimize(item)}
                                                disabled={optimizingId === item._id}
                                            >
                                                <i className={`fas ${optimizingId === item._id ? 'fa-spinner fa-spin' : 'fa-compress-alt'}`}></i>
                                            </button>
                                        </>
                                    )}
                                    {canDelete && (
                                        <button
                                            className="action-btn-icon delete"
                                            title="Delete"
                                            onClick={() => handleDelete(item._id)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {pagination && pagination.total > LIMIT && (
                <div className="media-pagination">
                    <span className="media-pagination-total">Total: {pagination.total}</span>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                    <span>Page {page} / {totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div>
                                <h2>Delete Media</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="warning-box" style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#b91c1c', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Warning</strong>
                                </div>
                                <p>Are you sure you want to delete this file?</p>
                                <p style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '0.5rem' }}>
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {resizeTarget && (
                <div className="modal-overlay-modern" onClick={() => setResizeTarget(null)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div><h2>Resize Image</h2></div>
                            <button className="modal-close" onClick={() => setResizeTarget(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                                Creates a new, separate resized copy — the original is never modified. Leave a field blank to scale proportionally.
                            </p>
                            <div className="form-group">
                                <label>Width (px)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={resizeForm.width}
                                    onChange={(e) => setResizeForm(f => ({ ...f, width: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label>Height (px)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={resizeForm.height}
                                    onChange={(e) => setResizeForm(f => ({ ...f, height: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setResizeTarget(null)}>Cancel</button>
                            <button className="btn-primary-modern" onClick={confirmResize}>
                                <i className="fas fa-crop-alt"></i> Resize
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Media;
